import BudgetRequestApi from '@/api/BudgetRequest'
import IOApi from '@/api/IOApi'
import dayjs from 'dayjs'

const assignIOBudgetModel = data => {
  return data.map(o => new IOBudgetModel(o))
}

export class IOBudgetModel {
  constructor({
    id,
    insertionOrderId,
    budget,
    netBudget,
    startTime,
    endTime,
    latestRequest,
    requests,
    resubmittable,
    tempId,
  } = {}) {
    this.id = Number(id) || undefined
    this.insertionOrderId = Number(insertionOrderId) || undefined
    this.budget = Number(budget) || 0
    this.netBudget = Number(netBudget) || 0
    this.startTime = startTime
    this.endTime = endTime
    this.latestRequest = new IOBudgetRequestModel(latestRequest)
    this.requests = (requests || []).map(
      request => new IOBudgetRequestModel(request),
    )
    // 初回申請前に使う疑似ID（String）
    this.tempId = tempId
    this.resubmittable = resubmittable
  }

  /**
   * 新規案件予算かどうか
   * @return {Boolean}
   */
  get isNew() {
    return typeof this.id === 'undefined'
  }

  /**
   * @return {boolean}
   */
  get isValid() {
    return this.startTime && this.endTime && this.budget > 0
  }

  /**
   * 最新の申請状況を返す。
   * - 新規で未申請のものは new を返す
   * @return {string} approved|rejected|waiting|cancel|new
   */
  get requestStatus() {
    if (!this.latestRequest) return 'new'
    return this.latestRequest.requestStatus
  }

  /**
   * 申請種別を返す
   * @return {string} new|update|delete
   */
  get requestType() {
    if (this.latestRequest.requestType === 'changed') return 'update'
    return this.latestRequest.requestType
  }

  /**
   * 変更申請が可能かどうか
   * - 新規申請でない
   * - かつ、申請ステータスが下記のいずれか
   *   - 却下
   *   - 承認済
   *   - 取り消し
   * - かつ、配信終了していないかどうか
   * @return {boolean}
   */
  get isRequestable() {
    return (
      !!this.id &&
      ['rejected', 'approved', 'cancel'].includes(this.requestStatus) &&
      dayjs(this.endTime).isAfter(dayjs())
    )
  }

  /**
   * 削除申請が可能かどうか
   * - 新規申請でない
   * - かつ、申請ステータスが "承認済" もしくは 申請ステータスが "却下"
   * - かつ、配信開始前かどうか
   * @return {boolean}
   */
  get isDeleteRequestable() {
    return (
      !!this.id &&
      (this.requestStatus === 'approved' ||
        this.requestStatus === 'rejected') &&
      dayjs(this.startTime).isAfter(dayjs())
    )
  }

  static async get({ insertionOrderId, campaignId }) {
    try {
      const ret = await IOApi.fetchIOBudgetList({
        IOId: insertionOrderId,
        campaignId,
      })
      return assignIOBudgetModel(ret.items)
    } catch (e) {
      console.error(e)
      throw e
    }
  }
  /**
   * 再申請が可能かどうか
   * - 新規申請
   * - かつ、申請ステータスが却下
   * @return {boolean}
   */
  get isResubmittable() {
    return this.resubmittable
  }

  /**
   * 申請前に編集が可能かどうか
   * - 新規申請
   * - 案件作成前（tempIdを持つ）
   * @return {boolean}
   */
  get isEditable() {
    return this.isNew && this.tempId
  }

  /**
   * 概要表示用のオブジェクトを返す。
   * - 新規申請（id = null）の場合は latestRequest から
   * - 既存予算の場合は承認済みデータから
   */
  get summary() {
    const { budget, startTime, endTime } = this.isNew
      ? this.latestRequest
      : this
    return {
      budget,
      startTime,
      endTime,
    }
  }
}

export class IOBudgetRequestModel {
  constructor({
    id,
    budget,
    startTime,
    endTime,
    requestStatus,
    requestType,
    reason,
    requestTime,
    requestAccountId,
    requestAccountName,
  } = {}) {
    this.id = Number(id) || undefined
    this.budget = budget
    this.startTime = startTime
    this.endTime = endTime
    this.requestStatus = requestStatus
    this.requestType = requestType
    this.reason = reason
    this.requestTime = requestTime
    this.requestAccountId = requestAccountId
    this.requestAccountName = requestAccountName
  }

  /**
   * 取り消し可能かどうか
   * @return {boolean}
   */
  get isCancelable() {
    return this.requestStatus === 'waiting'
  }

  /**
   * 取り消す
   */
  async cancel() {
    try {
      await BudgetRequestApi.cancel(this.id)
      this.requestStatus = 'cancel'
    } catch (e) {
      throw new Error('取り消せませんでした')
    }
  }
}
